import {
  BodyText,
  DetailText,
  Flex,
  IconContainer,
  Strong,
} from '@utilitywarehouse/hearth-react';
import {
  BroadbandSmallIcon,
  CashbackCardSmallIcon,
  ElectricitySmallIcon,
  InsuranceSmallIcon,
  MobileSmallIcon,
} from '@utilitywarehouse/hearth-react-icons';

import { PriceDetail } from '@features/details/Basket/types';
import { ProductType } from '@generated/gql/graphql';

import { capitalizeLabel } from './capitalizeLabel';

interface BasketItemProps {
  type: ProductType;
  total: string;
  charges?: string[];
  oneTimeCost?: PriceDetail;
  afterPromo?: PriceDetail;
}

const serviceIconMap = new Map([
  [
    ProductType.Energy,
    { icon: <ElectricitySmallIcon />, colorScheme: 'energy' as const },
  ],
  [
    ProductType.Broadband,
    { icon: <BroadbandSmallIcon />, colorScheme: 'broadband' as const },
  ],
  [
    ProductType.Mobile,
    { icon: <MobileSmallIcon />, colorScheme: 'mobile' as const },
  ],
  [
    ProductType.Insurance,
    { icon: <InsuranceSmallIcon />, colorScheme: 'insurance' as const },
  ],
  [
    ProductType.CashbackCard,
    { icon: <CashbackCardSmallIcon />, colorScheme: 'cashback' as const },
  ],
]);

const BasketItem: React.FC<BasketItemProps> = ({
  type,
  total,
  charges,
  oneTimeCost,
  afterPromo,
}) => {
  const iconConfig = serviceIconMap.get(type);

  return (
    <Flex spacing="xs" data-testid={`basket-item-${type.toLowerCase()}`}>
      {iconConfig && (
        <IconContainer
          size="sm"
          variant="subtle"
          colorScheme={iconConfig.colorScheme}
          marginRight="100"
        >
          {iconConfig.icon}
        </IconContainer>
      )}
      <Flex direction="column" spacing="sm" flex="1">
        <BodyText size="lg" weight="semibold">
          {capitalizeLabel(type.replace('_', ' '))}
        </BodyText>
        {(charges ?? []).map((charge) => (
          <BodyText key={charge} size="sm">
            {capitalizeLabel(charge)}
          </BodyText>
        ))}
      </Flex>
      <Flex direction="column" alignItems="end" spacing="xs" maxWidth="88px">
        <DetailText size="lg" color={afterPromo ? 'valid' : undefined}>
          {total}
        </DetailText>
        {oneTimeCost && (
          <BodyText size="sm" textAlign="right">
            {oneTimeCost.text} <Strong>{oneTimeCost.value}</Strong>{' '}
            {oneTimeCost.subtext}
          </BodyText>
        )}
        {afterPromo && (
          <BodyText size="sm" textAlign="right">
            {afterPromo.text} <Strong>{afterPromo.value}</Strong>{' '}
            {afterPromo.subtext}
          </BodyText>
        )}
      </Flex>
    </Flex>
  );
};

export { BasketItem, type BasketItemProps };
